import React, { useState } from 'react';
import { api } from '../../lib/api';
import { Button, useToast } from '../../ui/components';
import { useT } from '../../i18n';
import MentionTextarea from './MentionTextarea';
import { commentMentions, commentAttachments } from './CommentContent';
import './comments.css';

/** How long after posting a comment stays editable; mirrors the server-side window. */
export const COMMENT_EDIT_WINDOW_MS = 60 * 60 * 1000;

/** Whether the current user may edit this activity entry (server re-checks). */
export function canEditComment(activity, currentUserId) {
  return (
    activity?.type === 'Commented' &&
    !!currentUserId &&
    activity.actorUserId === currentUserId &&
    Date.now() - new Date(activity.createdAt).getTime() < COMMENT_EDIT_WINDOW_MS
  );
}

/**
 * CommentEditor — inline editor for one's own recent comment. Attachments stay as they are;
 * mentions can be added or removed, and newly added people get notified server-side.
 *
 * Props:
 *   basePath: document API path, e.g. `/requisitions/{id}`
 *   activity: the Commented activity entry being edited
 *   onSaved(updated): called with the updated activity
 *   onCancel: leave edit mode without saving
 */
export default function CommentEditor({ basePath, activity, onSaved, onCancel }) {
  const t = useT();
  const toast = useToast();
  const [body, setBody] = useState(activity.summary || '');
  // Prior mentions count as "inserted" so their tokens keep resolving; on save only
  // names still present in the text are sent, and the server emails only the new ones.
  const [mentions, setMentions] = useState(() => commentMentions(activity));
  const [saving, setSaving] = useState(false);

  const hasFiles = commentAttachments(activity).length > 0;
  const canSave = !saving && (body.trim().length > 0 || hasFiles);

  const save = async () => {
    if (!canSave) return;
    const text = body.trim();
    const ids = mentions.filter((m) => text.includes(`@${m.name}`)).map((m) => m.id);
    setSaving(true);
    try {
      const updated = await api.put(`${basePath}/comments/${activity.id}`, { body: text, mentions: ids });
      onSaved?.(updated);
    } catch (err) {
      toast.error(t('Could not update comment'), { description: err?.message });
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onCancel?.();
  };

  return (
    <div className="pp-comment-editor" onKeyDown={handleKeyDown}>
      <MentionTextarea
        value={body}
        onChange={setBody}
        rows={2}
        autoFocus
        placeholder={t('Edit your comment — type @ to mention a colleague…')}
        onMentionInserted={(user) =>
          setMentions((prev) => (prev.some((m) => m.id === user.id) ? prev : [...prev, user]))}
      />
      <div className="pp-comment-editor__actions">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          {t('Cancel')}
        </Button>
        <Button variant="secondary" size="sm" loading={saving} disabled={!canSave} onClick={save}>
          {t('Save')}
        </Button>
      </div>
    </div>
  );
}
